/* eslint-disable import/no-extraneous-dependencies */
// @refresh reset - Desabilita Fast Refresh para este arquivo para evitar reinicialização do Keycloak
import React, { useEffect } from 'react';
import Keycloak from 'keycloak-js';
import { AuthClientInitOptions } from '@react-keycloak/core';
import { useKeycloak } from '@react-keycloak/web';
import ConfigKeycloak from '../keycloak.json';
import SplashScreen from '../components/layout/SplashScreen';

// Constantes de configuração
const KEYCLOAK_TOKEN_REFRESH_SECONDS = 600; // 10 minutos

// Declaração global do Keycloak no window para evitar reinicializações
declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    __KEYCLOAK_INSTANCE__?: Keycloak.KeycloakInstance;
    __KEYCLOAK_INIT_FLAG__?: boolean;
  }
}

// Usar window global para armazenar instance e evitar reinicialização através de reloads
const getKeycloakInstance = (): Keycloak.KeycloakInstance => {
  const globalKey = '__KEYCLOAK_INSTANCE__';

  if (!window[globalKey]) {
    const instance = new Keycloak({
      url: process.env.REACT_APP_SSO_URL ?? ConfigKeycloak['auth-server-url'],
      realm: ConfigKeycloak.realm,
      clientId: ConfigKeycloak.resource,
    });

    // Armazena o estado original de inicialização
    let initPromise: Promise<boolean> | null = null;
    let hasInitialized = false;

    // Patch do método init para evitar múltiplas inicializações
    const originalInit = instance.init.bind(instance);
    instance.init = function initKeycloak(options: any) {
      // Se uma inicialização está em andamento, retorna a promise existente
      if (initPromise) {
        return initPromise;
      }

      // Se já foi inicializado com sucesso, retorna true
      if (hasInitialized) {
        return Promise.resolve(true);
      }

      // Marca como inicializando
      hasInitialized = true;

      // Cria a promise de inicialização
      initPromise = originalInit(options).catch((error: any) => {
        // Se houver erro, reseta os flags para permitir retry
        hasInitialized = false;
        initPromise = null;
        // Log do erro apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.warn('Keycloak initialization error:', error);
        }
        return false;
      });

      // Limpa a promise após conclusão
      initPromise.finally(() => {
        // Não limpa aqui para evitar race conditions
      });

      return initPromise;
    };

    // Também marca como __initialized para evitar que keycloak-js tente reinicializar
    // eslint-disable-next-line no-underscore-dangle
    (instance as any).__initialized = false;

    window[globalKey] = instance;
  }

  return window[globalKey]!;
};

export const keycloakInstance = getKeycloakInstance();

export const keycloakConfig: AuthClientInitOptions = {
  onLoad: 'login-required', // Force login instead of check-sso to avoid iframe issues
  checkLoginIframe: false,
  pkceMethod: 'S256', // Enable PKCE for better security
  flow: 'standard', // Use standard authorization code flow
};

interface IIsKeycloakInitializedProps {
  children: React.ReactNode;
}

function IsKeycloakInitialized({ children }: IIsKeycloakInitializedProps) {
  const { keycloak, initialized } = useKeycloak();

  useEffect(() => {
    let cancelled = false;

    if (keycloak && initialized) {
      keycloak.onTokenExpired = async () => {
        if (!cancelled) {
          await keycloak.updateToken(KEYCLOAK_TOKEN_REFRESH_SECONDS);
        }
      };
    }

    return () => {
      cancelled = true;
      if (keycloak) keycloak.onTokenExpired = () => null;
    };
  }, [initialized, keycloak]);

  // Renderiza conteúdo mesmo se não inicializado (para evitar bloqueio)
  // O useKeycloak hook vai tentar inicializar automaticamente
  if (!initialized) {
    return <SplashScreen />;
  }

  return children;
}

export { IsKeycloakInitialized };

export const userLevels: { [key: string]: string } = {
  '1': 'system',
  '2': 'partner',
  '3': 'client',
  '4': 'vendor',
};
